import React, { useMemo } from "react";
import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import Card from "components/card/Card.js";
import BarChart from "components/charts/BarChart";
import { GOLD_TEXT } from "constants/brandColors";
import REPORT_TIMEZONE from "constants/reportTimezone";

// Buckets are truncated in REPORT_TIMEZONE server-side, so label them in that
// same zone — any other zone would show e.g. Sep 28 for a Sep 29 bucket.
function formatBucket(bucket, interval) {
  const date = new Date(bucket);
  const timeZone = REPORT_TIMEZONE;
  if (interval === "hour") return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone });
  if (interval === "month") return date.toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone });
}

const PALETTE = ["#C7B45E", "#4318FF", "#FF5B5B", "#6AD2FF", "#8F7A2E"];

// `rows` is /reports/broadcast/trend's data: [{bucket, status, count}] — one
// row per (bucket, status) pair since `status` is a free-form string column
// on BotDispatchLog, not a fixed enum. Pivoted here into one stacked-bar
// series per distinct status actually present in the range.
export default function BroadcastTrendChart({ rows, interval }) {
  const { t } = useTranslation();

  const { categories, chartData } = useMemo(() => {
    const safeRows = rows || [];
    const buckets = [...new Set(safeRows.map((r) => r.bucket))].sort();
    const statuses = [...new Set(safeRows.map((r) => r.status))];

    const countFor = (bucket, status) =>
      safeRows.find((r) => r.bucket === bucket && r.status === status)?.count || 0;

    return {
      categories: buckets.map((b) => formatBucket(b, interval)),
      chartData: statuses.map((status) => ({
        name: status,
        data: buckets.map((b) => countFor(b, status)),
      })),
    };
  }, [rows, interval]);

  const chartOptions = useMemo(
    () => ({
      chart: { stacked: true, toolbar: { show: false } },
      colors: PALETTE,
      dataLabels: { enabled: false },
      tooltip: { theme: "light" },
      legend: { show: true, position: "top" },
      xaxis: {
        categories,
        labels: { style: { colors: "#A3AED0", fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: "#A3AED0" } } },
      grid: { strokeDashArray: 5 },
      plotOptions: { bar: { borderRadius: 6, columnWidth: "40%" } },
    }),
    [categories]
  );

  return (
    <Card w="100%" p="20px">
      <Text color={GOLD_TEXT} fontSize="xl" fontWeight="700" mb="10px">
        {t("Broadcast Dispatch Trend")}
      </Text>
      {rows?.length ? (
        <Box h="320px">
          <BarChart chartData={chartData} chartOptions={chartOptions} />
        </Box>
      ) : (
        <Text color="gray.500" textAlign="center" py={10}>{t("No data found")}</Text>
      )}
    </Card>
  );
}
